import axios from "axios";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
} from "react-query";
import { useRouter } from "next/router";
import { FaHeart, FaSearch } from "react-icons/fa";

import { NextSeo } from "next-seo";
import ModelCard2 from "../ModelCard2";
import styles from "../../styles/Model.module.css";
import { Input } from "@jammable/ui-components/Forms";
import { Spinner } from "@jammable/ui-components/Loader";
import NotFound from "../../components/NotFound";
import VoiceImage from "../VoiceImage";
import debounce from "lodash/debounce";
import { PiMicrophoneStageFill } from "react-icons/pi";
import { getAuth } from "firebase/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@jammable/ui-components/DropdownMenu";

const sortOptions = {
  hot: "usesLast24Hours",
  top: "uses",
  new: "createdAt",
};

interface Trending {
  isTrending: boolean;
  position: number | undefined;
}

export interface ApiListResponse<T> {
  data: T[];
  paging: {
    hasMore: boolean;
  };
  collections?: any[];
}

export interface Model {
  _id: string;
  artistname: string;
  uses: number;
  user: {
    username: string;
  };
  image: {
    url: string;
    blurhash?: string;
  };
  isLive: boolean;
  isPublic: boolean;
  urlParam: string;
  badges?: string[];
  accessIds: string[];
  likes?: number;
  trending: Trending;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

type PaginationParams = {
  page: number;
  search: string;
  sortBy: string;
  sortOrder: -1 | 1; // Assuming sortOrder can only be -1 or 1
  limit: number;
  hasMore: boolean;
};

type SortOption = keyof typeof sortOptions;

// Add a new interface for Badge
interface Badge {
  badge: string;
  count: number;
  emoji?: string;
  category?: string;
}

// Add a hook to fetch badges
function useBadges(search: string = "") {
  return useQuery<{ data: Badge[] }>(
    ["badges", search],
    async () => {
      const res = await axios.get("/api/model/getBadges", {
        params: { search },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useSearch(): [UseInfiniteQueryResult<ApiListResponse<Model>>, any] {
  const router = useRouter();
  const { q, filter, sort: sortQuery = "hot" } = router.query;

  const [searchInternal, setSearchInternal] = useState((q as string) || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchInternal);

  const sort = (
    Array.isArray(sortQuery) ? sortQuery[0] : sortQuery
  ) as SortOption;

  const validSort: SortOption = Object.prototype.hasOwnProperty.call(
    sortOptions,
    sort,
  )
    ? sort
    : "hot";

  // Debounce the search state update with a shorter delay for UI updates
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 250),
    [],
  );

  // Function to update both internal state immediately and trigger debouncedSetSearch
  const setSearch = useCallback(
    (value: string) => {
      setSearchInternal(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const setSort = (newSort: string) => {
    if (newSort === sort) return;
    if (newSort === "hot") {
      // Preserve other parameters when removing the sort parameter
      const newQuery = { ...router.query };
      delete newQuery.sort;
      router.push({
        pathname: router.pathname,
        query: newQuery,
      });
      return;
    }
    router.push({
      pathname: router.pathname,
      query: { ...router.query, sort: newSort },
    });
  };

  const setFilter = (newFilter: string) => {
    if (newFilter === filter) return;
    if (newFilter === "all") {
      const newQuery = { ...router.query };
      delete newQuery.filter;
      router.push({
        pathname: router.pathname,
        query: newQuery,
      });
      return;
    }
    router.push({
      pathname: router.pathname,
      query: { ...router.query, filter: newFilter },
    });
  };

  const limit = 24;

  async function fetchModelsSortedByUses({ pageParam = 1 }): Promise<{
    paging: PaginationParams;
    data: Model[];
  }> {
    const res = await axios.get(`/api/model`, {
      params: {
        filter,
        search: debouncedSearch, // Use the debounced search value for API calls
        limit,
        page: pageParam,
        sortOrder: "desc",
        sortBy: sortOptions[validSort],
      },
    });

    return res.data;
  }

  const models = useInfiniteQuery(
    ["all-models", debouncedSearch, filter, validSort], // Use debounced search in query key
    fetchModelsSortedByUses,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.paging.hasMore ? lastPage.paging.page + 1 : undefined;
      },
      // Don't refetch on window focus to avoid unexpected refreshes
      refetchOnWindowFocus: false,
    },
  );

  return [
    models,
    {
      value: searchInternal,
      setSearch,
      sort: validSort,
      setSort,
      filter,
      setFilter,
    },
  ];
}

const capitalizeFirstLetter = (value: string) => {
  return value?.charAt(0)?.toUpperCase() + value?.slice(1);
};

const ModelSearchPage = () => {
  const router = useRouter();

  const [fetching, setFetching] = useState(false);

  const [models, search] = useSearch();

  // Fetch badges
  const { data: badgesData, isLoading: badgesLoading } = useBadges();

  // Group badges by category
  const groupedBadges = useMemo(() => {
    if (!badgesData?.data) return {};

    return badgesData.data.reduce(
      (acc, badge) => {
        const category = badge.category || "Other";
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(badge);
        return acc;
      },
      {} as Record<string, Badge[]>,
    );
  }, [badgesData?.data]);

  // Sort categories for consistent display
  const sortedCategories = useMemo(() => {
    if (!groupedBadges) return [];

    // Define priority order for categories
    const categoryOrder = [
      "language",
      "entertainment",
      "character",
      "people",
      "Other",
    ];

    return Object.keys(groupedBadges).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);

      // If both categories are in the priority list, sort by priority
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one category is in the priority list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });
  }, [groupedBadges]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleScroll = async (e: any) => {
      if (
        !fetching &&
        window.innerHeight + e.target.documentElement.scrollTop + 800 >=
          e.target.documentElement.scrollHeight &&
        !models.isFetchingNextPage
      ) {
        setFetching(true);
        if (models.hasNextPage) {
          await models.fetchNextPage();
        }
        setFetching(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [models, fetching]);

  const updateQuery = useCallback(
    (newValue: string) => {
      const newQuery = { ...router.query };
      if (newValue) {
        newQuery.q = newValue;
      } else {
        delete newQuery.q;
      }
      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );
  const debouncedUpdateQuery = useMemo(
    () => debounce(updateQuery, 500),
    [updateQuery],
  );

  const title = `${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : ""
  }AI Voice Library | Jammable`;

  const description = `${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : "Jammable "
  }AI Voices. ${
    search?.filter ? `${capitalizeFirstLetter(search?.filter)} ` : ""
  }AI Cover Song Maker. Text To Speech. Most Advanced AI Technology. 50,000+ AI Cover Voices.`;

  let url = `https://www.jammable.com/models`;

  if (search?.filter && search?.value) {
    url = `https://www.jammable.com/models?filter=${search?.filter}&q=${search?.value}`;
  } else if (search?.filter) {
    url = `https://www.jammable.com/models?filter=${search?.filter}`;
  } else if (search?.value) {
    url = `https://www.jammable.com/models?q=${search?.value}`;
  }

  return (
    <div className="container">
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title: title,
          description: description,
          url: url,
        }}
      />
      <div className="md:tw-mt-6 tw-mt-2 tw-max-w-[1420px] tw-w-full tw-flex tw-h-full tw-flex-col tw-items-start tw-justify-start  tw-px-4">
        <div className="tw-mb-2 md:tw-mb-2 flex-gap-10 tw-flex tw-items-center tw-gap-2">
          <Input
            icon={<FaSearch size={20} />}
            inputClassName=" tw-rounded-l-full tw-pl-4 tw-text-gray-700"
            aria-label="Model Search Bar"
            value={search.value}
            onChange={(e) => {
              const newValue = e.target.value;
              search.setSearch(newValue);
              debouncedUpdateQuery(newValue);
            }}
            placeholder={`Search AI ${
              search.filter ? `${capitalizeFirstLetter(search.filter)} ` : ""
            }Voices...`}
            className="tw-w-full tw-rounded-full tw-h-12 md:tw-h-14 tw-bg-gray-100/80 tw-text-sm tw-pl-14"
            autoComplete="off"
            spellCheck={false}
          />

          {/* Category Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              style={{
                lineHeight: 1,
              }}
              className="tw-flex tw-items-center tw-justify-center tw-min-w-12 tw-min-h-12 tw-size-12 tw-text-lg md:tw-min-w-14 md:tw-min-h-14 md:tw-size-14 md:tw-text-xl hover:tw-bg-gray-200 tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-100 tw-transition-all tw-rounded-full tw-text-gray-700 tw-cursor-pointer"
            >
              {search.filter && badgesData?.data
                ? badgesData.data.find((b) => b.badge === search.filter)
                    ?.emoji || "🔍"
                : "🌎"}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="tw-w-60 tw-max-h-[50vh]  tw-overflow-y-auto tw-p-2 tw-rounded-3xl tw-shadow-2xl">
              {/* Sort selector */}
              <div className="tw-mb-3">
                <div className="tw-grid tw-grid-cols-2 tw-gap-1 tw-mb-2">
                  <DropdownMenuItem
                    className={`tw-px-3 tw-py-2 tw-rounded-2xl tw-cursor-pointer hover:tw-bg-gray-100 ${search.sort === "hot" ? "tw-bg-gray-50 tw-text-gray-700 tw-font-bold" : "tw-font-semibold"}`}
                    onClick={() => search.setSort("hot")}
                  >
                    <div className="tw-flex tw-items-center">
                      <span className="tw-mr-2 tw-text-xl">🔥</span>
                      <span>Hot</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={`tw-px-3 tw-py-2 tw-rounded-2xl tw-cursor-pointer hover:tw-bg-gray-100 ${search.sort === "new" ? "tw-bg-gray-50 tw-text-gray-700 tw-font-bold" : "tw-font-semibold"}`}
                    onClick={() => search.setSort("new")}
                  >
                    <div className="tw-flex tw-items-center">
                      <span className="tw-mr-2 tw-text-xl">✨</span>
                      <span>New</span>
                    </div>
                  </DropdownMenuItem>
                </div>
                <div className="tw-border-b tw-border-gray-200"></div>
              </div>
              <h3 className="tw-text-xs tw-font-black tw-uppercase tw-text-black/40 tw-tracking-widest tw-mb-1 tw-px-2">
                CATEGORIES
              </h3>
              <div className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-px-2">
                These categories are auto-generated.
              </div>
              <div className="tw-mb-3  tw-border-b tw-border-gray-200">
                <DropdownMenuItem
                  className={`tw-px-3 tw-py-2 tw-rounded-2xl tw-cursor-pointer hover:tw-bg-gray-100 ${!search.filter ? "tw-bg-gray-50 tw-text-gray-700 tw-font-bold" : " tw-font-semibold"}`}
                  onClick={() => search.setFilter("all")}
                >
                  <div className="tw-flex tw-items-center">
                    <span className="tw-mr-2 tw-text-xl">🌎</span>
                    <span>all voices</span>
                  </div>
                </DropdownMenuItem>
              </div>

              {badgesLoading ? (
                <div className="tw-px-4 tw-py-6 tw-text-center">
                  <Spinner size={4} className="tw-border-2" />
                </div>
              ) : (
                sortedCategories.map((category) => (
                  <div key={category} className="tw-mb-2">
                    <h3 className="tw-text-xs tw-font-black tw-uppercase tw-text-black/40 tw-tracking-widest tw-mb-1 tw-px-2">
                      {capitalizeFirstLetter(category)}
                    </h3>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-1">
                      {groupedBadges[category].map((badge) => (
                        <DropdownMenuItem
                          key={badge.badge}
                          className={`tw-px-2 tw-py-1.5 tw-rounded-2xl tw-cursor-pointer hover:tw-bg-gray-100 tw-flex tw-justify-between tw-items-center ${search.filter === badge.badge ? "tw-bg-gray-50 tw-text-gray-700 tw-font-bold" : "tw-font-semibold"}`}
                          onClick={() => search.setFilter(badge.badge)}
                        >
                          <div className="tw-flex tw-items-center">
                            <span className="tw-mr-1 tw-text-xl tw-w-7 tw-text-center">
                              {badge.emoji || "🔍"}
                            </span>
                            <span
                              style={{
                                lineHeight: "normal",
                              }}
                              className="tw-truncate"
                            >
                              {badge.badge}
                            </span>
                          </div>
                          <span className="tw-text-xs tw-bg-gray-200 tw-rounded-full tw-px-2 tw-py-1 tw-min-w-8 tw-text-center">
                            {badge.count}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* 
          <div
            onClick={() => {
              openModal(
                "filter-search-modal",
                <FilterSearchModal
                  sort={search.sort}
                  setSort={search.setSort}
                  filter={search.filter}
                  setFilter={search.setFilter}
                />,
              );
            }}
            className="tw-flex hover:tw-bg-gray-200 tw-border tw-border-solid tw-border-gray-200 tw-size-12 md:tw-size-14 tw-aspect-square tw-items-center tw-justify-center tw-bg-gray-100 tw-transition-all tw-rounded-full tw-text-gray-400 tw-cursor-pointer"
          >
            <FaFilter size={20} />
          </div> */}
        </div>

        {models?.isLoading && (
          <div className="loader-wrapper">
            <Spinner size={6} className="tw-border-3" />
          </div>
        )}
        {!models.isLoading && models?.data?.pages[0]?.data?.length === 0 && (
          <>
            <NotFound
              title="No voices found..."
              description={null}
              className="tw-my-10 "
              animateIn={false}
              buttonText={"Create your own voice!"}
              buttonLink={"/train"}
            />
          </>
        )}
        {models?.data?.pages[0]?.collections &&
          models?.data?.pages[0]?.collections?.length > 0 && (
            <h2 className="tw-text-2xl tw-font-bold tw-my-2">Collections</h2>
          )}

        <div className="tw-flex tw-w-full tw-gap-2 tw-overflow-x-scroll tw-scrollbar-h-1 tw-scrollbar-w-0 tw-scrollbar-thin tw-scrollbar-thumb-transparent tw-pb-0">
          {models?.data?.pages[0]?.collections?.map((collection, index) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {
              return (
                <Link
                  onClick={async () => {
                    try {
                      const lastSearchId =
                        sessionStorage.getItem("lastSearchId");
                      if (!lastSearchId) return;

                      const auth = getAuth();
                      const token = await auth.currentUser?.getIdToken();

                      await axios.post(
                        "/api/search/c",
                        {
                          searchId: lastSearchId,
                          collectionId: collection._id,
                        },
                        {
                          headers: token ? { Authorization: token } : undefined,
                        },
                      );
                    } catch (error) {}
                  }}
                  href={`/collection/${collection._id}`}
                  className="card-fade-in tw-opacity-0 tw-flex tw-relative tw-min-w-64 tw-h-36 tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-bg-transparent tw-p-2"
                  key={collection._id}
                  style={{
                    animationDelay: `${index * 0.025}s`,
                  }}
                >
                  <div className="tw-absolute tw-z-20 tw-bottom-0 tw-left-0 tw-w-full tw-h-full tw-bg-gradient-to-t tw-from-black/90 tw-to-transparent tw-rounded-b-3xl" />

                  <VoiceImage
                    model={collection}
                    className="tw-rounded-3xl"
                    fill={true}
                    style={{ objectFit: "cover" }}
                    sizes="(max-width: 710px) 100vw,
                    (max-width: 910px) 50vw,
                    (max-width: 1200px) 33vw,
                    (max-width: 1400px) 25vw,
                    15vw"
                    quality={75}
                    priority={false}
                    size={null}
                  />
                  <div className="tw-absolute tw-bottom-0 tw-left-0 tw-h-full tw-w-full tw-flex tw-flex-col tw-text-white tw-z-30 tw-rounded-b-2xl tw-p-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-justify-between">
                      <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-black tw-bg-black/60 tw-w-max tw-p-1 tw-rounded-full tw-px-2">
                        <span>{collection.likes}</span>
                        <FaHeart size={12} className="tw-text-red-500" />
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-black tw-bg-black/60 tw-w-max tw-p-1 tw-rounded-full tw-px-2">
                        <span>{collection.modelsCount}</span>
                        <PiMicrophoneStageFill size={14} />
                      </div>
                    </div>
                    <div className="tw-text-sm tw-font-bold tw-mt-auto">
                      {collection.title}
                    </div>
                    <div className="tw-text-xs tw-line-clamp-2 tw-text-white/80 tw-font-semibold">
                      {collection.description}
                    </div>
                  </div>
                </Link>
              );
            },
          )}
        </div>

        {models?.data?.pages[0]?.data &&
          models?.data?.pages[0]?.data?.length > 0 &&
          models?.data?.pages[0]?.collections &&
          models?.data?.pages[0]?.collections?.length > 0 && (
            <div>
              <h1 className="tw-text-2xl tw-font-bold tw-my-2">
                AI{" "}
                {search?.filter
                  ? `${capitalizeFirstLetter(search?.filter)} `
                  : ""}
                Voices
              </h1>
            </div>
          )}
        <div className={`${styles["model-page-wrapper"]} tw-mb-10`}>
          {models.data?.pages.map((page) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (page.data as any[]).map((model, index) => {
              return <ModelCard2 model={model} index={index} key={model._id} />;
            }),
          )}
        </div>
        {models.hasNextPage && (
          <div
            style={{ height: 80 }}
            className={
              models.isFetchingNextPage
                ? "loader-wrapper-models loader-wrapper-models-visible"
                : "loader-wrapper-models"
            }
          >
            <Spinner size={6} className="tw-border-3" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSearchPage;
